import type { ILaunchEvent } from '@/sanity/lib/definitions';
import { isCampaignWindowLive } from '@/lib/scheduledCampaign';

/**
 * Launch Event corner widget (see sanity/globals/LaunchEvent.ts).
 *
 * A single scheduled singleton drives one corner card on the homepage and on
 * every affiliate page during launch week. Like the Flash Sale it is an
 * overlay with no page-level opt-in: the clock passing the end date is the
 * rollback, and no document is ever mutated.
 */

/**
 * The two audiences the campaign serves. They are separate events: the German
 * one runs earlier than the international one, so each has its own end date,
 * its own countdown target and its own sign-up page.
 */
export interface ILaunchEventAudience {
  isEnabled?: boolean;
  startDate?: string;
  /** When the widget disappears for this audience. */
  endDate?: string;
  /** What the countdown counts down to for this audience. */
  countdownEndDate?: string;
  /** Sign-up path for this audience, before language prefixing. */
  ctaPath: string;
}

/**
 * Picks the audience a page belongs to.
 *
 * German pages — the German homepage and the German affiliate pages, i.e.
 * `settings.language === 'de'` — get the German event. Everything else
 * (English, Portuguese, any page with no language set) gets the international
 * event, which is the campaign's default set of fields.
 *
 * Every German field falls back to its international counterpart, so a
 * campaign configured only once still works everywhere; only the fields
 * marketing actually fills in diverge.
 */
export function resolveLaunchEventAudience(
  config: ILaunchEvent | null | undefined,
  language: string | undefined,
): ILaunchEventAudience | null {
  if (!config) return null;

  if (language === 'de') {
    const endDate = config.endDateDe || config.endDate;
    return {
      isEnabled: config.isEnabled,
      startDate: config.startDate,
      endDate,
      countdownEndDate: config.countdownEndDateDe || endDate,
      // An empty German path falls back to the international one; resolveLink
      // then adds the /de prefix, and addLanguagePrefix leaves an already
      // prefixed path such as "/de/launch-event" alone.
      ctaPath: config.ctaPathDe || config.ctaPath || '/launch-event',
    };
  }

  return {
    isEnabled: config.isEnabled,
    startDate: config.startDate,
    endDate: config.endDate,
    countdownEndDate: config.countdownEndDate || config.endDate,
    ctaPath: config.ctaPath || '/launch-event',
  };
}

/** True while this audience's window is open. */
export function isLaunchEventLive(
  audience: ILaunchEventAudience | null | undefined,
  now: number = Date.now(),
): boolean {
  return isCampaignWindowLive(audience, now);
}

/**
 * Pulls the partner code out of an affiliate page's checkout CTA.
 *
 * Affiliate pages carry their attribution in the hero CTA URL
 * (`...?aff=CODE`), which is what every checkout button on the page uses. The
 * launch-event sign-up runs the same automation keyed on `aff`, so the widget
 * has to forward the page's code rather than the visitor's — a visitor landing
 * on /trademania directly has no `aff` in their own URL.
 *
 * Returns undefined for an empty, relative or code-less URL; the widget then
 * falls back to whatever `resolveLink` picks up from the visitor's own session
 * (lib/utm.ts `getTrackingParams`).
 */
export function extractAffCode(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  try {
    // Relative CTA URLs are valid input — a base makes them parseable without
    // changing which query params we read.
    const parsed = new URL(url, 'https://www.iqcapital.io');
    return parsed.searchParams.get('aff') || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Appends the affiliate code to the already language-resolved sign-up link.
 *
 * `resolveLink` has run first, so the link may already carry `aff` (and UTMs)
 * from the visitor's own session. The page's own partner code wins when there
 * is one, matching the checkout CTAs on the same page.
 */
export function withAffCode(href: string, affCode: string | undefined): string {
  if (!href || !affCode) return href;
  try {
    const isAbsolute = /^https?:\/\//.test(href);
    const parsed = new URL(href, 'https://www.iqcapital.io');
    parsed.searchParams.set('aff', affCode);
    return isAbsolute
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return href;
  }
}
